import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, X, Loader2, Search, ClipboardList, Package, Truck,
  User, FileText, Hash,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface QuotationJob {
  id: string;
  number: string;
  type: string;
  description: string;
  quantity: number;
  unit?: string;
  status: string;
  assignedTo?: string;
  targetRate?: number;
  salesOrder: { id: string; number: string };
  _count?: { quotations: number };
  createdAt: string;
}

const STATUSES = ['open', 'in_progress', 'quoted', 'awarded'] as const;

const STATUS_CONFIG: Record<string, { label: string; accent: string; badgeBg: string; badgeText: string }> = {
  open: {
    label: 'Open',
    accent: '#94a3b8',       // slate-400
    badgeBg: 'rgba(148,163,184,0.15)',
    badgeText: '#94a3b8',
  },
  in_progress: {
    label: 'In Progress',
    accent: '#3b82f6',       // blue-500
    badgeBg: 'rgba(59,130,246,0.15)',
    badgeText: '#60a5fa',
  },
  quoted: {
    label: 'Quoted',
    accent: '#f59e0b',       // amber-500
    badgeBg: 'rgba(245,158,11,0.15)',
    badgeText: '#fbbf24',
  },
  awarded: {
    label: 'Awarded',
    accent: '#22c55e',       // green-500
    badgeBg: 'rgba(34,197,94,0.15)',
    badgeText: '#4ade80',
  },
};

const TYPE_CONFIG: Record<string, { label: string; color: string; Icon: typeof Package }> = {
  material: { label: 'Material', color: 'bg-purple-50 text-purple-700', Icon: Package },
  transport: { label: 'Transport', color: 'bg-orange-50 text-orange-700', Icon: Truck },
};

export function QuotationJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<QuotationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  // Create Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [salesOrders, setSalesOrders] = useState<any[]>([]);
  const [form, setForm] = useState({
    type: 'material',
    description: '',
    quantity: '',
    unit: '',
    targetRate: '',
    salesOrderId: '',
    assignedTo: '',
  });

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      params.page = String(page);
      params.limit = String(limit);
      const res = await api.get('/quotation-jobs', { params });
      setJobs(res.data.data?.quotationJobs ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load quotation jobs');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Fetch SOs for the create modal dropdown
  useEffect(() => {
    if (showModal) {
      api.get('/sales-orders?limit=50').then(r => {
        setSalesOrders(r.data.data ?? []);
      }).catch(() => {});
    }
  }, [showModal]);

  const jobsByStatus = (status: string) => {
    return jobs.filter((j) => j.status === status);
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/quotation-jobs', {
        type: form.type,
        description: form.description,
        quantity: parseFloat(form.quantity) || 0,
        unit: form.unit || undefined,
        targetRate: form.targetRate ? parseFloat(form.targetRate) : undefined,
        salesOrderId: form.salesOrderId || undefined,
        assignedTo: form.assignedTo || undefined,
      });
      setShowModal(false);
      setForm({ type: 'material', description: '', quantity: '', unit: '', targetRate: '', salesOrderId: '', assignedTo: '' });
      fetchJobs();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create quotation job');
    } finally {
      setSaving(false);
    }
  };

  const truncate = (text: string, max: number) => {
    if (text.length <= max) return text;
    return text.slice(0, max) + '...';
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Quotation Jobs
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Manage procurement quotation requests
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          New Job
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search quotation jobs..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-primary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading quotation jobs...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-red-500">
          <p className="text-sm">{error}</p>
          <button onClick={fetchJobs} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
        </div>
      ) : (
        /* Kanban Board */
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STATUSES.map((status) => {
            const config = STATUS_CONFIG[status];
            const statusJobs = jobsByStatus(status);
            return (
              <div
                key={status}
                className="flex w-72 flex-shrink-0 flex-col rounded-xl border overflow-hidden"
                style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)' }}
              >
                {/* Column Header — uses accent color strip for visual identity */}
                <div
                  className="px-4 py-3 border-b"
                  style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* Accent dot */}
                      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: config.accent }} />
                      <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {config.label}
                      </h3>
                    </div>
                    <span
                      className="rounded-full px-2.5 py-0.5 text-xs font-bold"
                      style={{ backgroundColor: config.badgeBg, color: config.badgeText }}
                    >
                      {statusJobs.length}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {statusJobs.length} job{statusJobs.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Cards */}
                <div className="flex-1 space-y-2 p-3 overflow-y-auto max-h-[calc(100vh-320px)]">
                  {statusJobs.length === 0 ? (
                    <div
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed py-8"
                      style={{ borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}
                    >
                      <ClipboardList size={24} className="mb-1 opacity-40" />
                      <p className="text-xs">No jobs</p>
                    </div>
                  ) : (
                    statusJobs.map((job) => {
                      const typeConf = TYPE_CONFIG[job.type] || TYPE_CONFIG.material;
                      const TypeIcon = typeConf.Icon;
                      return (
                        <div
                          key={job.id}
                          onClick={() => navigate(`/quotation-jobs/${job.id}`)}
                          className="rounded-lg border p-3 transition-all cursor-pointer hover:shadow-md"
                          style={{
                            backgroundColor: 'var(--bg-surface)',
                            borderColor: 'var(--border-primary)',
                          }}
                        >
                          {/* Job Number + Type Badge */}
                          <div className="mb-2 flex items-center justify-between">
                            <span className="text-xs font-semibold text-[var(--ink-0)]">{job.number}</span>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${typeConf.color}`}>
                              <TypeIcon size={10} />
                              {typeConf.label}
                            </span>
                          </div>

                          {/* Description */}
                          <p className="mb-2 text-sm font-medium leading-tight" style={{ color: 'var(--text-primary)' }}>
                            {truncate(job.description, 60)}
                          </p>

                          {/* Quantity */}
                          <div className="mb-2 flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <Hash size={12} />
                            <span>
                              Qty: {job.quantity}{job.unit ? ` ${job.unit}` : ''}
                            </span>
                          </div>

                          {/* SO + Quotation count */}
                          <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <span className="flex items-center gap-1">
                              <FileText size={12} />
                              SO: {job.salesOrder?.number || '-'}
                            </span>
                            <span className="flex items-center gap-1">
                              <ClipboardList size={12} />
                              {job._count?.quotations ?? 0} quote{(job._count?.quotations ?? 0) !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {/* Assigned To */}
                          {job.assignedTo && (
                            <div className="mt-2 flex items-center gap-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                              <User size={12} />
                              <span>{job.assignedTo}</span>
                            </div>
                          )}

                          {/* Created Date */}
                          <p className="mt-1.5 text-right text-xs" style={{ color: 'var(--text-muted)' }}>
                            {formatDate(job.createdAt)}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 mt-4">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div
            className="mx-4 w-full max-w-lg rounded-xl border shadow-xl max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 z-10 rounded-t-xl" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>New Quotation Job</h2>
              <button onClick={() => setShowModal(false)} style={{ color: 'var(--text-muted)' }} className="hover:opacity-70">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Type *</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="material">Material</option>
                  <option value="transport">Transport</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Description *</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe what needs to be procured..."
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Quantity *</label>
                  <input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    placeholder="0"
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Unit</label>
                  <input
                    type="text"
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    placeholder="e.g. kg, pcs, litres"
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Target Rate (INR)</label>
                  <input
                    type="number"
                    value={form.targetRate}
                    onChange={(e) => setForm({ ...form, targetRate: e.target.value })}
                    placeholder="0"
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Assigned To</label>
                  <input
                    type="text"
                    value={form.assignedTo}
                    onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}
                    placeholder="Person name"
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Linked Sales Order</label>
                <select
                  value={form.salesOrderId}
                  onChange={(e) => setForm({ ...form, salesOrderId: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                >
                  <option value="">None (standalone job)</option>
                  {salesOrders.map((so: any) => (
                    <option key={so.id} value={so.id}>{so.number} — {so.contact?.firstName || so.account?.name || 'Unknown'}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--border-primary)' }}>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.description || !form.quantity}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Job
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
